import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, CheckCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const resetToken = searchParams.get("token");
  
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-password-reset`, { email });
      setEmailSent(true);
      toast.success("Reset link sent to your email");
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      setResetComplete(true);
      toast.success("Password reset successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Reset failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Reset complete view
  if (resetComplete) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-serif text-2xl font-bold mb-2">Password Reset!</h1>
          <p className="text-muted-foreground mb-8">
            Your password has been successfully changed.
          </p>
          <Button
            onClick={() => navigate("/auth")}
            className="h-14 px-8 rounded-full bg-primary hover:bg-primary/90"
            data-testid="go-to-login-btn"
          >
            Go to Login
          </Button>
        </motion.div>
      </div>
    );
  }

  // Reset with token view
  if (resetToken) {
    return (
      <div className="min-h-screen flex flex-col p-6 md:p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-1 flex flex-col"
        >
          <button
            onClick={() => navigate("/auth")}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Login</span>
          </button>

          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl font-bold tracking-tight">
              Ventaphy<span className="text-primary">.</span>
            </h1>
          </div>

          <div className="flex-1">
            <div className="bg-card/50 backdrop-blur-sm rounded-3xl p-6 border border-border/50">
              <h2 className="font-serif text-2xl font-bold mb-2 text-center">
                Set New Password
              </h2>
              <p className="text-muted-foreground text-center mb-8">
                Choose a strong password
              </p>

              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-muted-foreground">
                    New Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-12 pl-12 rounded-xl bg-input/50 border-transparent focus:border-primary"
                      data-testid="new-password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-muted-foreground">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-12 pl-12 rounded-xl bg-input/50 border-transparent focus:border-primary"
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 rounded-full text-lg font-semibold bg-primary hover:bg-primary/90 mt-6"
                  data-testid="reset-password-btn"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Email sent view
  if (emailSent) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-serif text-2xl font-bold mb-2">Check Your Email</h1>
          <p className="text-muted-foreground mb-8">
            If an account exists with that email, you'll receive a reset link shortly.
          </p>
          <Button
            onClick={() => navigate("/auth")}
            variant="outline"
            className="h-12 px-8 rounded-full"
            data-testid="back-to-login-btn"
          >
            Back to Login
          </Button>
        </motion.div>
      </div>
    );
  }

  // Request reset view
  return (
    <div className="min-h-screen flex flex-col p-6 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex flex-col"
      >
        <button
          onClick={() => navigate("/auth")}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>

        <div className="text-center mb-8">
          <h1 className="font-serif text-3xl font-bold tracking-tight">
            Ventaphy<span className="text-primary">.</span>
          </h1>
        </div>

        <div className="flex-1">
          <div className="bg-card/50 backdrop-blur-sm rounded-3xl p-6 border border-border/50">
            <h2 className="font-serif text-2xl font-bold mb-2 text-center">
              Forgot Password?
            </h2>
            <p className="text-muted-foreground text-center mb-8">
              Enter your email and we'll send you a reset link
            </p>

            <form onSubmit={handleRequestReset} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-muted-foreground">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="h-12 pl-12 rounded-xl bg-input/50 border-transparent focus:border-primary"
                    data-testid="email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 rounded-full text-lg font-semibold bg-primary hover:bg-primary/90 mt-6"
                data-testid="send-reset-link-btn"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
